/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useEffect, useState } from 'react'
import { X, ExternalLink, TrendingUp, Award, Code, Brain, Eye, Globe } from 'lucide-react'
import { fetchAggregateLeaderboard, mapLeaderboardModelToHiveMind, type LLMLeaderboardData } from '@/lib/llm-leaderboards'

interface LLMLeaderboardModalProps {
  onClose: () => void
  onSelectModel?: (modelId: string) => void
  initialCategory?: 'general' | 'coding' | 'all'
}

export function LLMLeaderboardModal({ onClose, onSelectModel, initialCategory = 'all' }: LLMLeaderboardModalProps) {
  const [data, setData] = useState<LLMLeaderboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [category, setCategory] = useState<'general' | 'coding' | 'all'>(initialCategory)

  useEffect(() => {
    loadLeaderboard()
  }, [category])

  const loadLeaderboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchAggregateLeaderboard(category)
      setData(result)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectModel = (leaderboardModel: string) => {
    const hivemindModel = mapLeaderboardModelToHiveMind(leaderboardModel)
    if (hivemindModel && onSelectModel) {
      onSelectModel(hivemindModel)
      onClose()
    }
  }

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'coding': return <Code className="w-3 h-3" />
      case 'reasoning': return <Brain className="w-3 h-3" />
      case 'vision': return <Eye className="w-3 h-3" />
      case 'multilingual': return <Globe className="w-3 h-3" />
      default: return <TrendingUp className="w-3 h-3" />
    }
  }

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'coding': return 'text-blue-400'
      case 'reasoning': return 'text-purple-400'
      case 'vision': return 'text-green-400'
      case 'multilingual': return 'text-orange-400'
      default: return 'text-accent'
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        className="bg-background-secondary border border-border rounded-lg w-[900px] max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-secondary border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-accent" />
            <h2 className="text-lg font-title">LLM Leaderboard</h2>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://lmarena.ai/leaderboard"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-3 py-1 text-xs flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              LMArena
            </a>
            <a
              href="https://aider.chat/docs/leaderboards/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-3 py-1 text-xs flex items-center gap-1"
            >
              <Code className="w-3 h-3" />
              Aider
            </a>
            <button
              onClick={onClose}
              className="p-1 hover:bg-background-hover rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-condensed text-foreground-secondary">Category:</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as 'general' | 'coding' | 'all')}
                className="input text-xs px-2 py-1"
              >
                <option value="all">All Models</option>
                <option value="general">General Purpose</option>
                <option value="coding">Coding Specialists</option>
              </select>
            </div>
            <button
              onClick={loadLeaderboard}
              disabled={loading}
              className="btn-secondary px-3 py-1 text-xs"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-foreground-muted">Loading leaderboard data...</div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-400">
              Error: {error}
            </div>
          )}

          {/* Leaderboard Table */}
          {data && !loading && (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="bg-background-tertiary border border-border rounded p-3">
                <p className="text-xs text-foreground-secondary">
                  {data.description || 'Rankings aggregated from multiple leaderboard sources including LMArena, Aider.chat, and HuggingFace.'}
                </p>
                {data.lastUpdated && (
                  <p className="text-xs text-foreground-muted mt-1">
                    Last updated: {new Date(data.lastUpdated).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Table */}
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full">
                  <thead className="bg-background-tertiary">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Rank</th>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Model</th>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Organization</th>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Category</th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">
                        <div className="flex items-center justify-end gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Score
                        </div>
                      </th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">Context</th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">Cost/M</th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((entry, index) => {
                      const hivemindModel = mapLeaderboardModelToHiveMind(entry.model)
                      const isAvailable = !!hivemindModel

                      return (
                        <tr
                          key={index}
                          className={`border-t border-border ${isAvailable ? 'hover:bg-background-hover' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {entry.rank <= 3 && (
                                <Award className={`w-4 h-4 ${
                                  entry.rank === 1 ? 'text-yellow-400' :
                                  entry.rank === 2 ? 'text-gray-400' :
                                  'text-orange-400'
                                }`} />
                              )}
                              <span className="text-sm font-mono">{entry.rank}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium">{entry.model}</div>
                            {!isAvailable && (
                              <div className="text-xs text-foreground-muted">Not available in HiveMind</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-secondary">{entry.organization || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            {entry.category && (
                              <div className={`flex items-center gap-1 ${getCategoryColor(entry.category)}`}>
                                {getCategoryIcon(entry.category)}
                                <span className="text-xs capitalize">{entry.category}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-mono font-semibold text-accent">{entry.score.toFixed(1)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-xs font-mono text-foreground-secondary">
                              {entry.contextWindow ? `${(entry.contextWindow / 1000).toFixed(0)}K` : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-xs font-mono text-foreground-secondary">
                              {entry.costPerMToken ? `$${entry.costPerMToken.toFixed(2)}` : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isAvailable && onSelectModel ? (
                              <button
                                onClick={() => handleSelectModel(entry.model)}
                                className="btn-primary px-3 py-1 text-xs"
                              >
                                Use This
                              </button>
                            ) : (
                              <span className="text-xs text-foreground-muted">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer Info */}
              <div className="text-xs text-foreground-muted text-center pt-2">
                <p>
                  Data aggregated from{' '}
                  <a
                    href="https://lmarena.ai/leaderboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    LMArena
                  </a>
                  {', '}
                  <a
                    href="https://aider.chat/docs/leaderboards/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Aider.chat
                  </a>
                  {', '}
                  <a
                    href="https://huggingface.co/spaces/open-llm-leaderboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    HuggingFace
                  </a>
                  {', and '}
                  <a
                    href="https://artificialanalysis.ai/leaderboards/models"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Artificial Analysis
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


